import React from 'react';
import { useLocation } from 'react-router-dom';
import { Col, Row } from '../../lib/utils';
import { Head4, Body3Regular, Body3Bold, CaptionRegular } from '../../lib/font';
import { colors } from '../../lib/colors';
import { getPaymentRequest, formatPrice } from './data/dummyData';

const MemberPaymentCompletePage: React.FC = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const type = queryParams.get('type') || 'payment';
    const requestId = queryParams.get('requestId') || '';
    const appStoreUrl =
        'https://apps.apple.com/kr/app/rappo-%EA%B0%95%EC%82%AC%EC%99%80-%ED%9A%8C%EC%9B%90%EC%9D%B4-%EA%B0%80%EA%B9%8C%EC%9B%8C%EC%A7%80%EB%8A%94-%EA%B3%B5%EA%B0%84/id1635062466';
    const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.rappo';

    const isBilling = type === 'billing';
    const data = getPaymentRequest(requestId);

    const InfoRow: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
        <Row style={{ justifyContent: 'space-between', padding: '8px 0' }}>
            <CaptionRegular style={{ color: colors.LAVEL_2 }}>{label}</CaptionRegular>
            <Body3Regular style={{ color: highlight ? colors.primary : colors.LAVEL_4, fontWeight: highlight ? 600 : 400 }}>
                {value}
            </Body3Regular>
        </Row>
    );

    return (
        <Col style={{ width: '100%', minHeight: '100vh', alignItems: 'center', backgroundColor: '#F5F7FA' }}>
            <Col style={{ width: '100%', maxWidth: 480, minHeight: '100vh', backgroundColor: '#F5F7FA' }}>
                {/* 상단 성공 헤더 */}
                <Col
                    style={{
                        width: '100%',
                        padding: '40px 24px',
                        backgroundColor: isBilling ? '#059669' : colors.primary,
                        alignItems: 'center',
                        borderBottomLeftRadius: 24,
                        borderBottomRightRadius: 24,
                    }}
                >
                    <div
                        style={{
                            width: 64,
                            height: 64,
                            borderRadius: 32,
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {isBilling ? (
                            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                                <rect x="4" y="8" width="24" height="16" rx="3" stroke="#FFFFFF" strokeWidth="2.5" />
                                <path d="M4 14h24" stroke="#FFFFFF" strokeWidth="2.5" />
                            </svg>
                        ) : (
                            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                                <path d="M8 16l6 6 10-12" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        )}
                    </div>

                    <Head4 style={{ color: '#FFFFFF', marginTop: 16 }}>
                        {isBilling ? '카드가 등록되었어요' : '결제가 완료되었어요'}
                    </Head4>

                    {data && (
                        <Body3Regular style={{ color: 'rgba(255,255,255,0.85)', marginTop: 8, textAlign: 'center' }}>
                            {isBilling
                                ? `${data.memberName}님, 이제 수업 후 자동으로 결제됩니다`
                                : `${data.memberName}님의 목표, 함께 이루어 나가겠습니다`}
                        </Body3Regular>
                    )}
                </Col>

                <Col style={{ padding: '16px', gap: 12 }}>
                    {/* 선결제 완료: 결제 내역 */}
                    {!isBilling && data && (
                        <Col
                            style={{
                                width: '100%',
                                backgroundColor: '#FFFFFF',
                                borderRadius: 16,
                                padding: '24px 20px',
                                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                            }}
                        >
                            <Body3Bold style={{ color: colors.LAVEL_4 }}>{data.ticketName}</Body3Bold>
                            <Head4 style={{ color: colors.primary, marginTop: 4 }}>{formatPrice(data.totalPrice)}원</Head4>

                            <div style={{ width: '100%', height: 1, backgroundColor: '#F0F0F0', margin: '16px 0' }} />

                            <InfoRow label="수강권 종류" value={data.ticketType} />
                            <InfoRow label="담당 트레이너" value={data.trainerName} />
                            <InfoRow label="이용 기간" value={`${data.startDate} ~ ${data.endDate}`} />
                            <InfoRow label="이용 횟수" value={`${data.sessionCount}회`} />
                            <InfoRow label="이용 시작일" value={data.startDate} highlight />
                        </Col>
                    )}

                    {/* 카드등록 완료: 카드 정보 + 자동결제 안내 */}
                    {isBilling && data && (
                        <>
                            <Col
                                style={{
                                    width: '100%',
                                    backgroundColor: '#FFFFFF',
                                    borderRadius: 16,
                                    padding: '24px 20px',
                                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                                }}
                            >
                                <Body3Bold style={{ color: colors.LAVEL_4, marginBottom: 12 }}>등록 카드 정보</Body3Bold>
                                <InfoRow label="카드사" value="신한카드" />
                                <InfoRow label="카드번호" value="**** **** **** 1234" />
                            </Col>

                            <Col
                                style={{
                                    width: '100%',
                                    backgroundColor: '#FFFFFF',
                                    borderRadius: 16,
                                    padding: '24px 20px',
                                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                                }}
                            >
                                <Body3Bold style={{ color: colors.LAVEL_4, marginBottom: 12 }}>자동결제 안내</Body3Bold>
                                <CaptionRegular style={{ color: colors.LAVEL_3, lineHeight: '20px' }}>
                                    수업이 끝날 때마다 아래 금액이 자동으로 결제됩니다
                                </CaptionRegular>
                                <Row style={{ justifyContent: 'space-between', marginTop: 12, padding: '8px 0' }}>
                                    <CaptionRegular style={{ color: colors.LAVEL_2 }}>1회 수업료</CaptionRegular>
                                    <Body3Regular style={{ color: colors.primary, fontWeight: 600 }}>
                                        {formatPrice(data.perSessionPrice)}원
                                    </Body3Regular>
                                </Row>
                                <Row style={{ justifyContent: 'space-between', padding: '8px 0' }}>
                                    <CaptionRegular style={{ color: colors.LAVEL_2 }}>결제 시점</CaptionRegular>
                                    <Body3Regular style={{ color: colors.LAVEL_4 }}>수업 종료 후 자동</Body3Regular>
                                </Row>
                            </Col>
                        </>
                    )}

                    {/* 안내 문구 */}
                    <Col
                        style={{
                            width: '100%',
                            padding: '16px 20px',
                            backgroundColor: '#EFF6FF',
                            borderRadius: 12,
                        }}
                    >
                        <CaptionRegular style={{ color: '#1E40AF', textAlign: 'center', lineHeight: '20px' }}>
                            {isBilling
                                ? '지금은 결제된 금액이 없습니다\n첫 수업이 끝난 뒤 수업료가 자동으로 청구됩니다'
                                : '라포 앱에서 수강권을 확인해보세요'}
                        </CaptionRegular>
                    </Col>

                    {/* 앱 다운로드 버튼 */}
                    <button
                        onClick={() => window.open(appStoreUrl, '_blank')}
                        style={{
                            width: '100%',
                            height: 52,
                            borderRadius: 12,
                            border: 'none',
                            cursor: 'pointer',
                            backgroundColor: colors.primary,
                            color: '#FFFFFF',
                            fontSize: 16,
                            fontWeight: 700,
                            marginTop: 4,
                        }}
                    >
                        iOS 앱 다운로드
                    </button>
                    <button
                        onClick={() => window.open(playStoreUrl, '_blank')}
                        style={{
                            width: '100%',
                            height: 52,
                            borderRadius: 12,
                            border: `1.5px solid ${colors.primary}`,
                            cursor: 'pointer',
                            backgroundColor: '#FFFFFF',
                            color: colors.primary,
                            fontSize: 16,
                            fontWeight: 700,
                            marginTop: 8,
                        }}
                    >
                        Android 앱 다운로드
                    </button>
                </Col>
            </Col>
        </Col>
    );
};

export default MemberPaymentCompletePage;
